import React, { useEffect, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { CONFIGS } from '../../utilities/Configs';
import { ConstructFetchBody, FormatDate, UrlQueryParam } from '../../utilities/Helpers';
import { LocationFaucet, LocationKeg } from '../../utilities/Types';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { Delete as IconDelete } from '@material-ui/icons';

import { KegAdd } from './KegAdd';

type Props = {
	faucet: LocationFaucet,
	onEdit: () => void,
	onDeleteKeg: () => void,
	onClose: () => void
};

export const FaucetEdit = (props: Props) => {
	const [faucetData, setFaucetData] = useState<undefined | { brands: Array<any>, distributors: Array<any>, kegs: Array<any> }>(undefined);
	const [kegs, setKegs] = useState<undefined | Array<LocationKeg>>(undefined);
	const [isKegAddDialogOpen, setIsKegAddDialogOpen] = useState<boolean>(false);
	
	useEffect(() => {
        fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.LOCATION_FAUCET_SELECTION_LISTS_GET, [UrlQueryParam('token'), UrlQueryParam('id')])).then(
            (_response) => _response.json()
        ).then(
            (_response) => {
				setFaucetData({
					brands: _response.result.Brands,
					distributors: _response.result.Distributors,
					kegs: _response.result.Kegs
				});
			}
        ).catch(
            () => alert('An unexpected error has occurred. Please try again later. If problem persists, please contact support.')
        );

        fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.KEG_HISTORY_GET, [UrlQueryParam('token'), UrlQueryParam('id'), props.faucet['fid']])).then(
            (_response) => _response.json()
        ).then(
            (_response) => setKegs(_response['result'])
        ).catch(
            () => alert('An unexpected error has occurred. Please try again later. If problem persists, please contact support.')
        );
	}, []);
	
	const removeKeg = () => {
		if (window.confirm('Are you sure?') === true) {
			const post = [
				UrlQueryParam('id'),
				props.faucet['fid']
			];

			fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.KEG_DELETE, [...[UrlQueryParam('token')], ...post])).then(
				(_response) => _response.json()
			).then(
				(_response) => {
					if (_response['error'] !== undefined) {
						alert('Error: ' + _response['error']['message']);
					}
					else {
						props.onDeleteKeg();
						props.onClose();
					}
				}
			).catch(
				() => alert('An unexpected error has occurred. Please try again later. If problem persists, please contact support.')
			);
		}
	};

	const handleSubmit = (_values, _formikBag) => {
		const post = [
			UrlQueryParam('id'),
			props.faucet['fid'],
			(props.faucet['did'] || -1),
			(_values.brandId || -1),
			(_values.brandId !== props.faucet['bid'] ? -1 : (props.faucet['kid'] || -1))
		];

		fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.FAUCET_UPDATE, [...[UrlQueryParam('token')], ...post])).then(
			(_response) => _response.json()
		).then(
			(_response) => {
				if (_response['error'] !== undefined) {
					alert('Error: ' + _response['error']['message']);
				}
				else {
					props.onEdit();
					props.onClose();
				}
			}
		).catch(
			() => alert('An unexpected error has occurred. Please try again later. If problem persists, please contact support.')
		).finally(
			() => _formikBag.setSubmitting(false)
		);
	};

	const updateFaucet = (kegId) => {
		const post = [
			UrlQueryParam('id'),
			props.faucet['fid'],
			(props.faucet['did'] || -1),
			(props.faucet['bid'] || -1),
			kegId
		];

		fetch(CONFIGS.API.ENDPOINT, ConstructFetchBody(CONFIGS.API.METHODS.FAUCET_UPDATE, [...[UrlQueryParam('token')], ...post])).then(
			(_response) => _response.json()
		).then(
			(_response) => {
				if (_response['error'] !== undefined) {
					alert('Error: ' + _response['error']['message']);
				}
				else {
					props.onEdit();
					props.onClose();
				}
			}
		).catch(
			() => alert('An unexpected error has occurred. Please try again later. If problem persists, please contact support.')
		);
	};
	
	return (
		(faucetData === undefined || kegs === undefined) ? (
			<Dialog open>
				<DialogContent>
					Loading...
				</DialogContent>
			</Dialog>
		) : (
			<Formik
				initialValues={{
					brandId: (props.faucet['bid'] || '')
				}}
				onSubmit={handleSubmit}
				validateOnBlur={false}
				validateOnChange={false}
				validationSchema={
					Yup.object().shape({
						brandId: Yup.string()
					})
				}
			>
				{
					(_formik) => (
						<Dialog open maxWidth="sm" fullWidth>
							<DialogTitle>Manage Faucet</DialogTitle>
							<DialogContent dividers>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<Autocomplete
											options={faucetData.brands}
											filterOptions={createFilterOptions({
												limit: CONFIGS.FAUCET_EDIT.BRANDS_LIMIT,
												trim: true
											})}
											value={faucetData.brands.find((_brand) => (_brand['Key'] === _formik.values.brandId))}
											getOptionLabel={(_brand) => _brand['Value']}
											renderInput={
												(params) => (
													<TextField
														{...params}
														label="Brand"
														placeholder={(faucetData.brands.length > CONFIGS.FAUCET_EDIT.BRANDS_LIMIT ? 'Refine search, top ' + CONFIGS.FAUCET_EDIT.BRANDS_LIMIT + ' results shown...' : 'Select...')}
														InputLabelProps={{
															shrink: true
														}}
														variant="outlined"
														margin="dense"
													/>
												)
											}
											onChange={(_event, _brand) => (_brand !== null ? _formik.setFieldValue('brandId', _brand['Key']) : () => {})}
										/>
									</Grid>
									<Grid item xs={12}>
										<div className="row spread">
											<p className="bold">Keg</p>
											<div className="row center">
												<IconButton color="secondary" disabled={(props.faucet['kid'] === null)} onClick={removeKeg}>
													<IconDelete />
												</IconButton>
												<Button variant="contained" color="primary" disabled={props.faucet['bid'] === null} onClick={() => setIsKegAddDialogOpen(true)}>New Keg</Button>
											</div>
										</div>
									</Grid>
									<Grid item xs={12}>
										<table>
											<thead>
												<tr>
													<th style={{ width: '25%' }}>Size</th>
													<th style={{ width: '25%' }}>Installed</th>
													<th style={{ width: '25%' }}>Born</th>
													<th style={{ width: '25%' }}>Expires</th>
												</tr>
											</thead>
											<tbody>
												{
													kegs.length === 0 ? (
														<tr>
															<td colSpan={4}>None</td>
														</tr>
													) : (
														kegs.map(
															(_keg, _i) => (
																<tr key={_i}>
																	<td>
																		{
																			(() => {
																				switch(_keg['keg_size']) {
																					case 1:
																						return 'Small';

																					case 2:
																						return 'Large';
																						
																					default:
																						return 'None';
																				}
																			})()
																		}
																	</td>
																	<td>{_keg['tapped_date'] !== null ? FormatDate(new Date(_keg['tapped_date'])) : '-'}</td>
																	<td>{_keg['born_on_date'] !== null ? FormatDate(new Date(_keg['born_on_date'])) : '-'}</td>
																	<td>{_keg['expiration_date'] !== null ? FormatDate(new Date(_keg['expiration_date'])) : '-'}</td>
																</tr>
															)
														)
													)
												}
											</tbody>
										</table>
									</Grid>
								</Grid>
							</DialogContent>
							<DialogActions>
								<Button variant="contained" color="default" disabled={_formik.isSubmitting} onClick={props.onClose}>Cancel</Button>
								<Button variant="contained" color="primary" disabled={_formik.isSubmitting} onClick={() => _formik.handleSubmit()}>Save</Button>
							</DialogActions>
							{
								isKegAddDialogOpen === true && (
									<KegAdd faucet={props.faucet} brand={faucetData.brands.find((_brand) => (_brand['Key'] === props.faucet['bid']))} onAdd={(_kegId) => updateFaucet(_kegId)} onClose={() => setIsKegAddDialogOpen(false)} />
								)
							}
						</Dialog>
					)
				}
			</Formik>
		)
	);
};